import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Card,
  Button,
  Form,
  Input,
  Select,
  Switch,
  ColorPicker,
  InputNumber,
  Tabs,
  Row,
  Col,
  Typography,
  Space,
  message,
  Tooltip,
  Divider,
  Tag,
  Badge,
  Alert,
  Spin,
  theme
} from 'antd';
import {
  SaveOutlined,
  EyeOutlined,
  SettingOutlined,
  UpOutlined,
  DownOutlined,
  InfoCircleOutlined,
  FormatPainterOutlined,
  FileTextOutlined,
  PrinterOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useSelection } from '../../SelectionContext';
import { getAuthorizedCompanyContext } from '../../utils/companyContext';
import API_CONFIG from '../../config/api';
import { canonicalPayslipElementCode, normalizePayslipElementAliases } from '../../services/pdf/PayslipTemplateRendering';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

interface PayrollElement {
  id?: number;
  code: string;
  name: string;
  custom_description?: string;
  type: 'gain' | 'deduction' | 'info' | 'total' | 'saved';
  category?: string;
  calculation_type?: string;
  display_order: number;
  is_active?: boolean;
  presentation_only?: 'total' | 'saved';
}

export interface PayslipTemplate {
  id?: number;
  current_version?: number;
  name: string;
  description?: string;
  template_type: 'professional' | 'compact' | 'detailed' | 'custom';
  is_default: boolean;
  elements_config: {
    [key: string]: {
      visible: boolean;
      display_order: number;
      label_override?: string;
      format?: 'currency' | 'percentage' | 'text';
    };
  };
  style_config: {
    logo_position: 'left' | 'center' | 'right';
    company_info_format: 'detailed' | 'compact';
    colors: {
      primary: string;
      secondary: string;
      accent: string;
    };
    fonts: {
      family: string;
      sizes: {
        title: number;
        header: number;
        body: number;
        small: number;
      };
    };
  };
  print_config: {
    page_size: 'A4' | 'Letter';
    orientation: 'portrait' | 'landscape';
    margin: string;
    show_logo: boolean;
    show_company_details: boolean;
    show_employee_photo: boolean;
    show_signature_area: boolean;
    show_stamp_area: boolean;
    show_iuts_breakdown?: boolean;
    show_calculation_details?: boolean;
  };
}

interface PayslipTemplateBuilderProps {
  visible: boolean;
  onCancel: () => void;
  templateId?: number;
  onSave?: (template: PayslipTemplate) => void;
}

const summaryLabels: Record<string, string> = {
  TOTAL_GAINS: 'Total des gains', GROSS_SALARY: 'Salaire brut', TAXABLE_BASE: 'Base imposable',
  TOTAL_DEDUCTIONS: 'Total des retenues', NET_SALARY: 'Net à payer'
};
const requiredTotals = new Set(Object.keys(summaryLabels));

const createEmptyTemplate = (): PayslipTemplate => ({
  name: '', description: '', template_type: 'professional', is_default: false,
  elements_config: {},
  style_config: {
    logo_position: 'left', company_info_format: 'detailed',
    colors: { primary: '#2c5282', secondary: '#e2e8f0', accent: '#1890ff' },
    fonts: { family: 'Helvetica', sizes: { title: 20, header: 14, body: 12, small: 10 } }
  },
  print_config: {
    page_size: 'A4', orientation: 'portrait', margin: '12mm', show_logo: true,
    show_company_details: true, show_employee_photo: false, show_signature_area: true,
    show_stamp_area: true, show_iuts_breakdown: false, show_calculation_details: false
  }
});

// Un montant historisé peut porter plusieurs anciens codes; il doit avoir un seul contrôle.
export const normalizePayslipElementCatalog = (
  elements: PayrollElement[], savedConfig: PayslipTemplate['elements_config'] = {}
): PayrollElement[] => {
  const catalog = new Map<string, PayrollElement>();
  const informationalCodes = new Set(['IUTS_ZERO', ...elements
    .filter(element => element.type === 'info').map(element => canonicalPayslipElementCode(element.code))]);
  elements.forEach(element => {
    const code = canonicalPayslipElementCode(element.code);
    if (element.type === 'info' || informationalCodes.has(code)) return;
    if (!catalog.has(code) || element.code === code) {
      catalog.set(code, {
        ...element, code,
        ...(code === 'AUTRES_PRIMES' ? {
          name: 'Autres indemnités (regroupées)',
          custom_description: 'Les anciens codes Autres primes, Autres indemnités et Indemnité de caisse correspondent à un montant historique regroupé. Leur présentation ne peut pas être séparée sur ces bulletins.'
        } : {})
      });
    }
  });
  const canonicalSaved: PayslipTemplate['elements_config'] = normalizePayslipElementAliases(savedConfig);
  Object.entries(canonicalSaved).forEach(([code, config]) => {
    if (!catalog.has(code) && !informationalCodes.has(code) && !requiredTotals.has(code)) {
      catalog.set(code, { code, name: code, type: 'saved', display_order: config.display_order,
        presentation_only: 'saved' });
    }
  });
  Object.entries(summaryLabels).forEach(([code, name], index) => {
    catalog.set(code, { code, name, type: 'total', display_order: 10000 + index, presentation_only: 'total' });
  });
  return Array.from(catalog.values());
};

// Les modèles historiques peuvent ne contenir qu'une partie des options.
export const hydratePayslipTemplate = (saved: Partial<PayslipTemplate> = {}, elements: PayrollElement[] = []): PayslipTemplate => {
  const defaults = createEmptyTemplate();
  const elementsConfig: PayslipTemplate['elements_config'] = normalizePayslipElementAliases(saved.elements_config || {});
  normalizePayslipElementCatalog(elements, elementsConfig).forEach((element, index) => {
    elementsConfig[element.code] = Object.assign({
      visible: true, display_order: element.display_order ?? index + 1, format: 'currency' as const
    }, elementsConfig[element.code]);
    if (requiredTotals.has(element.code)) elementsConfig[element.code].visible = true;
  });
  return {
    ...defaults, ...saved, is_default: Boolean(saved.is_default), elements_config: elementsConfig,
    style_config: {
      ...defaults.style_config, ...saved.style_config,
      colors: { ...defaults.style_config.colors, ...saved.style_config?.colors },
      fonts: {
        ...defaults.style_config.fonts, ...saved.style_config?.fonts,
        sizes: { ...defaults.style_config.fonts.sizes, ...saved.style_config?.fonts?.sizes }
      }
    },
    print_config: { ...defaults.print_config, ...saved.print_config }
  };
};

const PayslipTemplateBuilder: React.FC<PayslipTemplateBuilderProps> = ({ visible, onCancel, templateId, onSave }) => {
  const { token, hasPermission } = useAuth();
  const { selectedCompany } = useSelection();
  const { token: themeToken } = theme.useToken();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [availableElements, setAvailableElements] = useState<PayrollElement[]>([]);
  const [template, setTemplate] = useState<PayslipTemplate>(createEmptyTemplate);
  const [previewHTML, setPreviewHTML] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const requestVersion = useRef(0);
  const canSave = hasPermission(`Ressources Humaines.Templates.Bulletins.${templateId ? 'edit' : 'create'}`);
  const canPreview = canSave || hasPermission('Ressources Humaines.Templates.Bulletins.preview');
  const requestHeaders = () => {
    const companyId = selectedCompany ? getAuthorizedCompanyContext() : null;
    if (selectedCompany && !companyId) throw new Error('Sélectionnez une entreprise accessible pour charger les bulletins et leurs modèles.');
    return {
      Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
      ...(companyId ? { 'X-Company-Id': String(companyId) } : {})
    };
  };

  useEffect(() => {
    const version = ++requestVersion.current;
    const controller = new AbortController();
    setPreviewOpen(false);
    setPreviewHTML('');
    setLoadError('');
    setSaving(false);
    setPreviewing(false);
    setTemplate(createEmptyTemplate());
    setAvailableElements([]);
    form.resetFields();
    if (!visible || !token) return () => controller.abort();
    setLoading(true);
    const readJSON = async (path: string) => {
      const response = await fetch(API_CONFIG.url(path), { headers: requestHeaders(), signal: controller.signal });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Impossible de charger le modèle');
      return data.data;
    };
    Promise.all([
      readJSON('/api/hr/payslip-templates/available-elements'),
      templateId ? readJSON(`/api/hr/payslip-templates/${templateId}`) : Promise.resolve({})
    ]).then(([elements, saved]) => {
      if (version !== requestVersion.current || controller.signal.aborted) return;
      const catalog = normalizePayslipElementCatalog(elements, saved.elements_config);
      const loaded = hydratePayslipTemplate(saved, catalog);
      setTemplate(loaded);
      setAvailableElements(catalog.sort((a: PayrollElement, b: PayrollElement) => {
        if (requiredTotals.has(a.code) && requiredTotals.has(b.code)) {
          return Object.keys(summaryLabels).indexOf(a.code) - Object.keys(summaryLabels).indexOf(b.code);
        }
        return Number(requiredTotals.has(a.code)) - Number(requiredTotals.has(b.code)) ||
          loaded.elements_config[a.code].display_order - loaded.elements_config[b.code].display_order;
      }));
      form.setFieldsValue(loaded);
    }).catch((error) => {
      if (version !== requestVersion.current || controller.signal.aborted) return;
      setLoadError(error.message || 'Erreur lors du chargement du modèle');
    }).finally(() => {
      if (version === requestVersion.current && !controller.signal.aborted) setLoading(false);
    });
    return () => { controller.abort(); requestVersion.current += 1; };
    // Le contexte doit être rechargé même si l'utilisateur garde le même modèle ouvert.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, templateId, token, selectedCompany, form]);

  const handleSave = async () => {
    if (!canSave || loading || loadError || saving) return;
    const version = requestVersion.current;
    try {
      setSaving(true);
      const values = await form.validateFields();
      if (version !== requestVersion.current) return;
      const payload = { ...template, ...values, ...(template.current_version !== undefined ? { expected_version: template.current_version } : {}) };
      const path = templateId ? `/api/hr/payslip-templates/${templateId}` : '/api/hr/payslip-templates';
      const response = await fetch(API_CONFIG.url(path), {
        method: templateId ? 'PUT' : 'POST', headers: requestHeaders(), body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (version !== requestVersion.current) return;
      if (!response.ok || !data.success) throw new Error(data.error || 'Erreur lors de la sauvegarde');
      message.success(templateId ? 'Modèle mis à jour' : 'Modèle créé');
      onSave?.(hydratePayslipTemplate({ ...payload, ...data.data }));
      onCancel();
    } catch (error: any) {
      if (version === requestVersion.current && !error.errorFields) message.error(error.message || 'Erreur lors de la sauvegarde');
    } finally {
      if (version === requestVersion.current) setSaving(false);
    }
  };

  // Déplacer un élément vers le haut
  const moveElementUp = (index: number) => {
    if (index === 0 || requiredTotals.has(availableElements[index].code) || requiredTotals.has(availableElements[index - 1].code)) return;

    const items = [...availableElements];
    [items[index], items[index - 1]] = [items[index - 1], items[index]];

    // Mettre à jour les ordres d'affichage
    const newConfig = { ...template.elements_config };
    items.forEach((item, idx) => {
      if (newConfig[item.code] && !requiredTotals.has(item.code)) {
        newConfig[item.code] = { ...newConfig[item.code], display_order: idx + 1 };
      }
    });

    setTemplate(prev => ({
      ...prev,
      elements_config: newConfig
    }));

    setAvailableElements(items);
  };

  // Déplacer un élément vers le bas
  const moveElementDown = (index: number) => {
    if (index === availableElements.length - 1 || requiredTotals.has(availableElements[index].code) || requiredTotals.has(availableElements[index + 1].code)) return;

    const items = [...availableElements];
    [items[index], items[index + 1]] = [items[index + 1], items[index]];

    // Mettre à jour les ordres d'affichage
    const newConfig = { ...template.elements_config };
    items.forEach((item, idx) => {
      if (newConfig[item.code] && !requiredTotals.has(item.code)) {
        newConfig[item.code] = { ...newConfig[item.code], display_order: idx + 1 };
      }
    });

    setTemplate(prev => ({
      ...prev,
      elements_config: newConfig
    }));

    setAvailableElements(items);
  };

  // Toggle visibilité d'un élément
  const toggleElementVisibility = (code: string) => {
    if (requiredTotals.has(code)) return;
    setTemplate(prev => ({
      ...prev,
      elements_config: {
        ...prev.elements_config,
        [code]: {
          ...prev.elements_config[code],
          visible: !prev.elements_config[code]?.visible
        }
      }
    }));
  };

  // Mettre à jour le label d'un élément
  const updateElementLabel = (code: string, label: string) => {
    setTemplate(prev => ({
      ...prev,
      elements_config: {
        ...prev.elements_config,
        [code]: {
          ...prev.elements_config[code],
          label_override: label
        }
      }
    }));
  };

  // Prévisualisation du template
  const handlePreview = async () => {
    if (!canPreview || loading || loadError || previewing) return;
    const version = requestVersion.current;
    setPreviewing(true);
    try {
      const values = form.getFieldsValue();
      const response = await fetch(API_CONFIG.url('/api/hr/payslip-templates/preview'), {
        method: 'POST', headers: requestHeaders(),
        body: JSON.stringify({ ...template, ...values, name: values.name?.trim() || 'Aperçu du bulletin' })
      });
      const data = await response.json();
      if (version !== requestVersion.current) return;
      if (!response.ok || !data.success || typeof data.data?.html !== 'string') {
        throw new Error(data.error || 'Impossible de générer l’aperçu');
      }
      setPreviewHTML(data.data.html);
      setPreviewOpen(true);
    } catch (error: any) {
      if (version === requestVersion.current) message.error(error.message || 'Erreur lors de l’aperçu');
    } finally {
      if (version === requestVersion.current) setPreviewing(false);
    }
  };

  // Composant pour la configuration des éléments
  const ElementsConfiguration = () => (
    <Card title="Configuration des Éléments de Paie" size="small" styles={{ title: { whiteSpace: 'normal' } }}>
      <Alert
        message="Configuration des éléments"
        description="Réorganisez les rubriques et adaptez leurs libellés. Masquer une rubrique ne change ni les calculs ni les totaux enregistrés. Les totaux restent affichés ; leur libellé peut être adapté. Les informations de calcul se règlent dans l’onglet Impression via « Détails de calcul »."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <div>
        {availableElements.map((element, index) => {
          const config = template.elements_config[element.code] || { visible: true, display_order: index + 1 };

          return (
            <Card
              key={element.code}
              size="small"
              style={{
                marginBottom: 8,
                backgroundColor: themeToken.colorBgContainer
              }}
              styles={{ body: { padding: '8px 12px' } }}
            >
              <Row gutter={[8, 8]} align="middle" justify="space-between">
                <Col xs={2} sm={2}>
                  <Space direction="vertical" size="small">
                    <Button
                      type="text"
                      size="small"
                      icon={<UpOutlined />}
                      onClick={() => moveElementUp(index)}
                      disabled={index === 0 || requiredTotals.has(element.code) || !canSave || loading || saving}
                      aria-label={`Monter ${element.name}`}
                      style={{ padding: 0, minWidth: 20, height: 20 }}
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={<DownOutlined />}
                      onClick={() => moveElementDown(index)}
                      disabled={index === availableElements.length - 1 || requiredTotals.has(element.code) || requiredTotals.has(availableElements[index + 1]?.code || '') || !canSave || loading || saving}
                      aria-label={`Descendre ${element.name}`}
                      style={{ padding: 0, minWidth: 20, height: 20 }}
                    />
                  </Space>
                </Col>

                <Col xs={22} sm={12}>
                  <div>
                    <Text strong style={{ color: config.visible ? themeToken.colorText : themeToken.colorTextSecondary }}>
                      {element.name}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {element.code}{element.category ? ` - ${element.category}` : ''}
                    </Text>
                    {element.custom_description && <div><Text type="secondary" style={{ fontSize: 12 }}>{element.custom_description}</Text></div>}
                    {requiredTotals.has(element.code) && <Tag>Total conservé</Tag>}
                    {element.presentation_only === 'saved' && <Tag>Rubrique mémorisée</Tag>}
                    {!requiredTotals.has(element.code) && element.type === 'gain' && (
                      <Tag color="green">Gain</Tag>
                    )}
                    {!requiredTotals.has(element.code) && element.type === 'deduction' && (
                      <Tag color="red">Retenue</Tag>
                    )}
                  </div>
                </Col>

                <Col xs={18} sm={6}>
                  <Input
                    size="small"
                    aria-label={`Libellé de ${element.name}`}
                    maxLength={150}
                    placeholder="Libellé personnalisé"
                    value={config.label_override || ''}
                    onChange={(e) => updateElementLabel(element.code, e.target.value)}
                    style={{ marginBottom: 4 }}
                  />
                </Col>

                <Col xs={6} sm={4}>
                  <Switch
                    size="small"
                    aria-label={`Afficher ${element.name}`}
                    disabled={requiredTotals.has(element.code) || !canSave || loading || saving}
                    checked={requiredTotals.has(element.code) || config.visible}
                    onChange={() => toggleElementVisibility(element.code)}
                    checkedChildren="Visible"
                    unCheckedChildren="Masqué"
                  />
                </Col>
              </Row>
            </Card>
          );
        })}
      </div>
    </Card>
  );

  // Composant pour la configuration de style
  const StyleConfiguration = () => (
    <Row gutter={16}>
      <Col xs={24} md={12}>
        <Card title="Couleurs" size="small" styles={{ title: { whiteSpace: 'normal' } }}>
          <Form.Item label="Couleur principale">
            <ColorPicker
              disabledAlpha
              value={template.style_config.colors.primary}
              onChange={(color) => setTemplate(prev => ({
                ...prev,
                style_config: {
                  ...prev.style_config,
                  colors: {
                    ...prev.style_config.colors,
                    primary: color.toHexString()
                  }
                }
              }))}
            />
          </Form.Item>

          <Form.Item label="Couleur secondaire">
            <ColorPicker
              disabledAlpha
              value={template.style_config.colors.secondary}
              onChange={(color) => setTemplate(prev => ({
                ...prev,
                style_config: {
                  ...prev.style_config,
                  colors: {
                    ...prev.style_config.colors,
                    secondary: color.toHexString()
                  }
                }
              }))}
            />
          </Form.Item>

          <Form.Item label="Couleur d'accentuation">
            <ColorPicker
              disabledAlpha
              value={template.style_config.colors.accent}
              onChange={(color) => setTemplate(prev => ({
                ...prev,
                style_config: {
                  ...prev.style_config,
                  colors: {
                    ...prev.style_config.colors,
                    accent: color.toHexString()
                  }
                }
              }))}
            />
          </Form.Item>
        </Card>
      </Col>

      <Col xs={24} md={12}>
        <Card title="Polices et Tailles" size="small" styles={{ title: { whiteSpace: 'normal' } }}>
          <Form.Item label="Famille de police" extra="Le PDF utilise Helvetica pour Arial et Inter, et Times pour Times New Roman.">
            <Select
              value={template.style_config.fonts.family}
              onChange={(value) => setTemplate(prev => ({
                ...prev,
                style_config: {
                  ...prev.style_config,
                  fonts: {
                    ...prev.style_config.fonts,
                    family: value
                  }
                }
              }))}
            >
              {template.style_config.fonts.family === 'Inter' && <Option value="Inter">Inter (historique)</Option>}
              <Option value="Arial">Arial</Option>
              <Option value="Helvetica">Helvetica</Option>
              <Option value="Times New Roman">Times New Roman</Option>
            </Select>
          </Form.Item>

          <Row gutter={8}>
            <Col xs={24} md={12}>
              <Form.Item label="Titre">
                <InputNumber
                  size="small"
                  min={14}
                  max={30}
                  value={template.style_config.fonts.sizes.title}
                  onChange={(value) => setTemplate(prev => ({
                    ...prev,
                    style_config: {
                      ...prev.style_config,
                      fonts: {
                        ...prev.style_config.fonts,
                        sizes: {
                          ...prev.style_config.fonts.sizes,
                          title: value || 20
                        }
                      }
                    }
                  }))}
                  addonAfter="pt"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="En-tête">
                <InputNumber
                  size="small"
                  min={10}
                  max={20}
                  value={template.style_config.fonts.sizes.header}
                  onChange={(value) => setTemplate(prev => ({
                    ...prev,
                    style_config: {
                      ...prev.style_config,
                      fonts: {
                        ...prev.style_config.fonts,
                        sizes: {
                          ...prev.style_config.fonts.sizes,
                          header: value || 14
                        }
                      }
                    }
                  }))}
                  addonAfter="pt"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col xs={24} md={12}>
              <Form.Item label="Corps">
                <InputNumber
                  size="small"
                  min={8}
                  max={16}
                  value={template.style_config.fonts.sizes.body}
                  onChange={(value) => setTemplate(prev => ({
                    ...prev,
                    style_config: {
                      ...prev.style_config,
                      fonts: {
                        ...prev.style_config.fonts,
                        sizes: {
                          ...prev.style_config.fonts.sizes,
                          body: value || 12
                        }
                      }
                    }
                  }))}
                  addonAfter="pt"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Petit">
                <InputNumber
                  size="small"
                  min={6}
                  max={12}
                  value={template.style_config.fonts.sizes.small}
                  onChange={(value) => setTemplate(prev => ({
                    ...prev,
                    style_config: {
                      ...prev.style_config,
                      fonts: {
                        ...prev.style_config.fonts,
                        sizes: {
                          ...prev.style_config.fonts.sizes,
                          small: value || 10
                        }
                      }
                    }
                  }))}
                  addonAfter="pt"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  );

  // Composant pour la configuration d'impression
  const PrintConfiguration = () => (
    <Row gutter={16}>
      <Col xs={24} md={12}>
        <Card title="Mise en page" size="small" styles={{ title: { whiteSpace: 'normal' } }}>
          <Form.Item label="Taille de page">
            <Select
              value={template.print_config.page_size}
              onChange={(value) => setTemplate(prev => ({
                ...prev,
                print_config: {
                  ...prev.print_config,
                  page_size: value as 'A4' | 'Letter'
                }
              }))}
            >
              <Option value="A4">A4</Option>
              <Option value="Letter">Letter</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Orientation">
            <Select
              value={template.print_config.orientation}
              onChange={(value) => setTemplate(prev => ({
                ...prev,
                print_config: {
                  ...prev.print_config,
                  orientation: value as 'portrait' | 'landscape'
                }
              }))}
            >
              <Option value="portrait">Portrait</Option>
              <Option value="landscape">Paysage</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Marges">
            <Input
              value={template.print_config.margin}
              onChange={(e) => setTemplate(prev => ({
                ...prev,
                print_config: {
                  ...prev.print_config,
                  margin: e.target.value
                }
              }))}
              placeholder="12mm"
            />
          </Form.Item>

          <Form.Item label="Informations de l’entreprise">
            <Select value={template.style_config.company_info_format} onChange={(value) => setTemplate(prev => ({
              ...prev, style_config: { ...prev.style_config, company_info_format: value }
            }))}>
              <Option value="detailed">Détaillées</Option>
              <Option value="compact">Compactes</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Position du logo">
            <Select
              value={template.style_config.logo_position}
              onChange={(value) => setTemplate(prev => ({
                ...prev,
                style_config: {
                  ...prev.style_config,
                  logo_position: value as 'left' | 'center' | 'right'
                }
              }))}
            >
              <Option value="left"><AlignLeftOutlined /> Gauche</Option>
              <Option value="center"><AlignCenterOutlined /> Centre</Option>
              <Option value="right"><AlignRightOutlined /> Droite</Option>
            </Select>
          </Form.Item>
        </Card>
      </Col>

      <Col xs={24} md={12}>
        <Card title="Éléments à afficher" size="small" styles={{ title: { whiteSpace: 'normal' } }}>
          <Form.Item>
            <Switch
              checked={template.print_config.show_logo}
              onChange={(checked) => setTemplate(prev => ({
                ...prev,
                print_config: {
                  ...prev.print_config,
                  show_logo: checked
                }
              }))}
            />
            <Text style={{ marginLeft: 8 }}>Afficher le logo</Text>
          </Form.Item>

          <Form.Item>
            <Switch
              checked={template.print_config.show_company_details}
              onChange={(checked) => setTemplate(prev => ({
                ...prev,
                print_config: {
                  ...prev.print_config,
                  show_company_details: checked
                }
              }))}
            />
            <Text style={{ marginLeft: 8 }}>Détails de l'entreprise</Text>
          </Form.Item>

          <Form.Item>
            <Switch
              checked={template.print_config.show_employee_photo}
              onChange={(checked) => setTemplate(prev => ({
                ...prev,
                print_config: {
                  ...prev.print_config,
                  show_employee_photo: checked
                }
              }))}
            />
            <Text style={{ marginLeft: 8 }}>Photo de l'employé</Text>
          </Form.Item>

          <Form.Item>
            <Switch
              checked={template.print_config.show_signature_area}
              onChange={(checked) => setTemplate(prev => ({
                ...prev,
                print_config: {
                  ...prev.print_config,
                  show_signature_area: checked
                }
              }))}
            />
            <Text style={{ marginLeft: 8 }}>Zone de signature</Text>
          </Form.Item>

          <Form.Item>
            <Switch
              checked={template.print_config.show_stamp_area}
              onChange={(checked) => setTemplate(prev => ({
                ...prev,
                print_config: {
                  ...prev.print_config,
                  show_stamp_area: checked
                }
              }))}
            />
            <Text style={{ marginLeft: 8 }}>Zone de cachet</Text>
          </Form.Item>

          <Form.Item>
            <Switch
              checked={template.print_config.show_iuts_breakdown || false}
              onChange={(checked) => setTemplate(prev => ({
                ...prev,
                print_config: {
                  ...prev.print_config,
                  show_iuts_breakdown: checked
                }
              }))}
            />
            <Text style={{ marginLeft: 8 }}>Détail calcul IUTS</Text>
          </Form.Item>

          <Form.Item>
            <Switch
              checked={template.print_config.show_calculation_details || false}
              onChange={(checked) => setTemplate(prev => ({
                ...prev,
                print_config: {
                  ...prev.print_config,
                  show_calculation_details: checked
                }
              }))}
            />
            <Text style={{ marginLeft: 8 }}>Détails de calcul</Text>
          </Form.Item>
        </Card>
      </Col>
    </Row>
  );

  const visibleElementsCount = availableElements.filter(element => requiredTotals.has(element.code) || template.elements_config[element.code]?.visible).length;
  const totalElementsCount = availableElements.length;

  return (
    <Modal
      title={
        <Space wrap>
          <SettingOutlined style={{ color: themeToken.colorPrimary }} />
          <span>{templateId ? 'Modifier le modèle' : 'Créer un modèle'}</span>
          <Badge count={visibleElementsCount} showZero style={{ backgroundColor: themeToken.colorSuccess }}>
            <Tag color="blue">{totalElementsCount} éléments</Tag>
          </Badge>
        </Space>
      }
      open={visible}
      onCancel={saving ? undefined : onCancel}
      closable={!saving}
      maskClosable={!saving}
      width={1200}
      style={{ top: 20 }}
      footer={[
        <Button key="cancel" disabled={saving} onClick={onCancel}>
          Annuler
        </Button>,
        <Button
          key="preview"
          icon={<EyeOutlined />}
          onClick={handlePreview}
          loading={previewing}
          disabled={loading || !!loadError || !canPreview || saving}
        >
          Aperçu
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
          disabled={loading || !!loadError || !canSave}
        >
          {templateId ? 'Mettre à jour' : 'Créer'}
        </Button>
      ]}
    >
      {loadError && <Alert type="error" showIcon message={loadError} style={{ marginBottom: 16 }} />}
      {!canSave && <Alert type="info" showIcon message="Vous ne disposez pas du droit d’enregistrer ce modèle." style={{ marginBottom: 16 }} />}
      <Spin spinning={loading}>
      <Form
        form={form}
        layout="vertical"
        disabled={loading || saving || !!loadError || !canSave}
        initialValues={template}
        onValuesChange={(_, allValues) => {
          setTemplate(prev => ({ ...prev, ...allValues }));
        }}
      >
        <Row gutter={16}>
          <Col xs={24} md={18}>
            <Form.Item
              name="name"
              label="Nom du modèle"
              rules={[{ required: true, whitespace: true, message: 'Le nom est requis' }, { max: 100, message: '100 caractères maximum' }]}
            >
              <Input placeholder="Ex : Bulletin standard" maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="template_type" label="Type">
              <Select>
                <Option value="professional">Professionnel</Option>
                <Option value="compact">Compact</Option>
                <Option value="detailed">Détaillé</Option>
                <Option value="custom">Personnalisé</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="description" label="Description">
          <TextArea
            rows={2}
            placeholder="Description du modèle..."
          />
        </Form.Item>

        <Form.Item>
          <Form.Item name="is_default" valuePropName="checked" noStyle>
            <Switch />
          </Form.Item>
          <Text style={{ marginLeft: 8 }}>Modèle par défaut</Text>
          <Tooltip title="Un seul modèle peut être marqué par défaut par entreprise">
            <InfoCircleOutlined style={{ marginLeft: 4, color: themeToken.colorPrimary }} />
          </Tooltip>
        </Form.Item>

        <Divider />

        <Tabs defaultActiveKey="elements" type="card">
          <TabPane
            tab={
              <span>
                <FileTextOutlined />
                Éléments de Paie
                <Badge count={visibleElementsCount} size="small" style={{ marginLeft: 4 }} />
              </span>
            }
            key="elements"
          >
            {ElementsConfiguration()}
          </TabPane>

          <TabPane
            tab={
              <span>
                <FormatPainterOutlined />
                Style
              </span>
            }
            key="style"
          >
            {StyleConfiguration()}
          </TabPane>

          <TabPane
            tab={
              <span>
                <PrinterOutlined />
                Impression
              </span>
            }
            key="print"
          >
            {PrintConfiguration()}
          </TabPane>
        </Tabs>
      </Form>
      </Spin>
      <Modal title="Aperçu du modèle" open={previewOpen && visible} onCancel={() => setPreviewOpen(false)} footer={null} width={1000}>
        <Alert type="info" showIcon message="Exemple fictif pour vérifier la présentation. Les montants du bulletin réel proviennent de la paie enregistrée." style={{ marginBottom: 16 }} />
        <iframe title="Aperçu du bulletin de paie" sandbox="" referrerPolicy="no-referrer" srcDoc={previewHTML} style={{ width: '100%', height: '65vh', border: `1px solid ${themeToken.colorBorder}`, background: '#fff' }} />
      </Modal>
    </Modal>
  );
};

export default PayslipTemplateBuilder;
